import { FC, ReactNode, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  CircularProgress,
  InputAdornment,
  TextField,
  Typography
} from "@mui/material";
import { UserDto } from "../../api/Api";
import { useUserSearch } from "../traderAccess/userSearchQuery";

type Props = {
  value: UserDto | null;
  onChange: (user: UserDto | null) => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  startIcon?: ReactNode;
};

// A value restored from a stored field may carry only a name (no Graph id), so fall back to it.
const sameUser = (a: UserDto, b: UserDto) =>
  a.id && b.id ? a.id === b.id : a.name === b.name;

/**
 * Single tenant user picked through the Graph-backed user search. Shared by the trader access
 * grant dialog and the "contact person from MFT trading team" fields on every book.
 */
export const UserPicker: FC<Props> = ({
  value,
  onChange,
  label = "User",
  placeholder = "Search by name or email",
  required = false,
  disabled = false,
  startIcon
}) => {
  const [input, setInput] = useState("");
  const { data: searchResults, isFetching } = useUserSearch(input);

  const results = searchResults ?? [];
  // The current value must be among `options` or MUI warns about a value it cannot match, but
  // only the search results are listed (filterOptions below), so it never shows as a stray row.
  const options =
    value && !results.some((r) => sameUser(r, value))
      ? [value, ...results]
      : results;

  return (
    <Autocomplete<UserDto>
      options={options}
      value={value}
      onChange={(_, next) => onChange(next)}
      inputValue={input}
      onInputChange={(_, next) => setInput(next)}
      getOptionLabel={(option) => option.name ?? ""}
      isOptionEqualToValue={sameUser}
      filterOptions={() => results}
      loading={isFetching}
      disabled={disabled}
      noOptionsText={
        input.trim().length < 2 ? "Type at least 2 characters" : "No users found"
      }
      renderOption={(props, option) => (
        <li {...props} key={option.id ?? option.name}>
          <Box display="flex" flexDirection="column" minWidth={0}>
            <Typography variant="body2" noWrap>
              {option.name}
            </Typography>
            {option.email && (
              <Typography variant="caption" color="text.secondary" noWrap>
                {option.email}
              </Typography>
            )}
          </Box>
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          required={required}
          slotProps={{
            input: {
              ...params.InputProps,
              startAdornment: startIcon ? (
                <>
                  <InputAdornment position="start">{startIcon}</InputAdornment>
                  {params.InputProps.startAdornment}
                </>
              ) : (
                params.InputProps.startAdornment
              ),
              endAdornment: (
                <>
                  {isFetching ? <CircularProgress size={16} /> : null}
                  {params.InputProps.endAdornment}
                </>
              )
            }
          }}
        />
      )}
    />
  );
};

type NameProps = Omit<Props, "value" | "onChange"> & {
  value: string;
  onChange: (name: string) => void;
};

/**
 * UserPicker for fields that store the picked user's display name as a plain string, such as
 * contactPersonTradingTeam. Older free-text values still show as the selected value.
 */
export const UserNamePicker: FC<NameProps> = ({ value, onChange, ...rest }) => {
  // Stable identity per name: Autocomplete resets the typed input whenever the value object
  // changes, so a fresh object on every render would wipe each keystroke.
  const user = useMemo(() => (value ? { name: value } : null), [value]);
  return (
    <UserPicker
      {...rest}
      value={user}
      onChange={(next) => onChange(next?.name ?? "")}
    />
  );
};
